import { Card } from "@/shared/ui/card.tsx";
import { Compass, Copy, Download, Forward, ImagePlus, RefreshCcw } from "lucide-react";
import { format, parse } from "date-fns";
import { Skeleton } from "@/shared/ui/skeleton.tsx";
import ConfirmScheduleDialog from "../ConfirmScheduleDialog";
import { SocialPostType } from "../../types";
// import { Dialog, DialogContent, DialogTrigger } from "@/shared/dialog";
import EditPostSheet from "@/shared/edit-post-with-gallery/EditPostSheet";
// import { useContext } from "react";
// import { ApprovalContext, ApprovalContextType } from "../../context";
// import ImageLoader from "@/shared/image-loader";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/ui/tooltip.tsx";
import { IconStarFilled } from "@tabler/icons-react";
import { useAtomValue } from "jotai";
import { themeAtom } from "@/features/app-layout/atoms/theme-atoms";
// import { Button } from "@/shared/button";
import GalleryDialog from "@/shared/gallery/GalleryDialog";
import { Dialog, DialogContent, DialogTrigger } from "@/shared/ui/dialog.tsx";
import { Button } from "@/shared/ui/button.tsx";
import DateCheckDialog from "@/shared/ui/date-check-dialog.tsx";
import { getTrueDate } from "@/lib/utils";


type SocialCardWithImageProps = {
  content: SocialPostType["content"][number],
  event_id: number
  event_name: string,
  event_status: "Scheduled" | "Pending",
  isRegenerating: boolean,
  onSchedule: (id: number) => void,
  eventIndex: number,
  isPlatformSelected: boolean,
  onSliceChange?: () => void
}

const SocialCardWithImage = ({ content, event_status, isRegenerating, event_name, event_id, onSchedule, eventIndex, isPlatformSelected, onSliceChange }: SocialCardWithImageProps) => {

  const theme = useAtomValue(themeAtom)

  // const { isImageLoading, eventId } = useContext<ApprovalContextType>(ApprovalContext)

  // const { discardPost } = useDiscardPost()

  return (
    <Card className="break-inside-avoid w-full group relative mt-1 p-5 pb-3 flex flex-col justify-around gap-3 rounded-2xl">
      <div className="absolute top-1 right-2 flex items-center gap-2">

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="hidden lg:flex items-center gap-1">{[1, 2, 3, 4, 5].map((_, index) => (
                <IconStarFilled key={index} size={14} color="#ffd000" />
              ))}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>High retention post</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <img src={`https://cdn.simpleicons.org/${(content.platform.toLowerCase().includes("twitter")) ? "x" : content.platform.toLowerCase()}`} className=" w-6 h-6" />
      </div>
      <div className="flex items-center gap-2">
        <img src={theme.avatar} className="w-7 aspect-square rounded-full" />
        <div className="flex items-center gap-2">
          <p className="text-sm flex items-center gap-2 font-semibold">{theme.handle}
            <img src="https://86v8u6agau.ufs.sh/f/A17KOoeuO4h9AcrbE4uO4h9zUp1a6MJqneWdFYX87yo52wmj" alt="" className="w-4" />
          </p>
          <p className="text-xs text-gray-400 font-poppins font-medium"> {format(parse(content.post_time, "HH:mm:ss", new Date()), "hh:mm a")} {" "}
            | {getTrueDate(content.post_date, 'MMM dd, yyyy')}</p>
        </div>
      </div>
      {!isRegenerating && <p className="text-xs lg:text-sm !leading-6 tracking-wide">{content.post_content} {content.tags.map((item) => `${item} `)}</p>}
      {isRegenerating && <div className="flex flex-col size-full gap-2">
        {[1, 2, 3, 4, 5, 6].map(num => (
          <Skeleton key={num} className="w-full h-6" />
        ))}
      </div>}
      {content.image_url !== null &&
        <Dialog>
          <DialogTrigger>
            <div className="size-full relative group">
              <img
                src={content.image_url}
                loading="lazy"
                className="w-full h-fit rounded-lg cursor-pointer"
                alt="Preview"
                width="500px"
                height="500px"
              />
            </div>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-black text-purple-500 font-semibold border-none outline-black p-0">
            <div className="absolute bottom-2 right-2 flex items-center gap-2">
              <Button size="icon"
                title="Download Image"
                className=""
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = content.image_url;
                  link.download = content.image_url.split('/').pop() || 'image';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}>
                <Download />
              </Button>
              <Button size="icon"
                onClick={() => {
                  navigator.clipboard.writeText(content.image_video_recommendation)
                }}
              >
                <Copy />
              </Button>
            </div>
            <img src={content.image_url} className="w-full h-auto rounded-lg" alt="Full View" />
          </DialogContent>
        </Dialog>
      }

      <div className="pt-2 border-t flex items-center justify-between gap-1 px-2">
        <div className="flex items-center gap-1 sm:gap-2">
          <div
            style={{ 
              display: isPlatformSelected ? "none" : "flex",
              backgroundImage: `radial-gradient(${theme.primary}, ${theme.secondary}, ${theme.alternate})`
            }}
            onClick={() => {
              if (onSliceChange) {
                onSliceChange()
              }
            }}
            className="flex gap-1 hover:bg-neutral-200 cursor-pointer items-center text-gray-500 border border-transparent p-[0.5px] rounded-full">
            <div className="flex gap-1 items-center w-full h-full bg-white px-2 py-1 rounded-full">
              <RefreshCcw size={13} />
              <span className="text-[0.6rem] hidden sm:block lg:text-xs animate-wave">Regenerate</span>
            </div>
          </div>
          <GalleryDialog content={content}
            trigger={
              <div
                style={{ backgroundImage: `radial-gradient(${theme.primary}, ${theme.secondary}, ${theme.alternate})` }}
                className="flex gap-1 hover:bg-neutral-200 cursor-pointer items-center text-gray-500 border border-transparent p-[0.5px] rounded-full">
                <div className="flex gap-1 items-center w-full h-full bg-white px-2 py-1 rounded-full">
                  <ImagePlus size={13} />
                  <span className="text-[0.6rem] hidden sm:block md:hidden xl:block 2xl:hidden 3xl:block lg:text-xs animate-wave">Select</span>
                </div>
              </div>}
          />
        </div>
        <div className="flex items-center gap-1 xl:gap-2">
          {isRegenerating && <>
            <Skeleton className="w-8 h-3" />
            <Skeleton className="w-8 h-3" />
            <Skeleton className="w-8 h-3" />
          </>}
          {!isRegenerating && <>
            <EditPostSheet
              location="review"
              variant="pending"
              onSchedule={onSchedule}
              postDetails={{ ...content, image_ids: [] }}
              eventDetails={{ event_id, event_name, story_id: 0 }}
              trigger={<div className="flex gap-1 hover:bg-neutral-200 rounded-xl cursor-pointer py-1 px-2 items-center text-gray-500">
                <Compass size={13} />
                <span className="text-[0.6rem] lg:text-xs">Edit</span>
              </div>}
            />
            {event_status === "Pending" &&
              (new Date(content.post_date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)) ?
              <DateCheckDialog data={content} onSchedule={onSchedule} location="review" eventIndex={event_id} /> :
              <ConfirmScheduleDialog
                post_id={content.post_id}
                eventIndex={eventIndex}
                onSchedule={onSchedule}
                trigger={
                  <button
                    disabled={!content.image_url}
                    type="button"
                    title={content.image_url ? "Schedule on social media" : "Select image to process"}
                    className="flex gap-1 hover:bg-neutral-20- disabled:bg-neutral-200 rounded-lg cursor-pointer py-1 px-1 items-center text-gray-500">
                    <Forward size={13} />
                    <span className="text-[0.6rem] lg:text-xs">Approve</span>
                  </button>
                } />
            }
          </>}
        </div>
      </div>
    </Card>
  )
}

export default SocialCardWithImage;
